import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ApiError } from "@/lib/api";
import { useContact } from "./hooks";
import { ContactHeader } from "./ContactHeader";
import { ActionBar } from "./ActionBar";
import { ContactInfoCard } from "./ContactInfoCard";
import { CustomFieldsCard, DetailsGrid } from "./DetailsGrid";
import { RightRail } from "./RightRail";
import { PinnedNotes, Timeline } from "./Timeline";
import { BillingView, EmailHistory, FilesView } from "./DetailViews";

export type ContactView = "timeline" | "billing" | "email" | "files";

/** The one main contact screen: header, action bar, details grid, right rail, timeline, and the
 *  three detail views (Billing, Email history, Files & archive). */
export function ContactPage() {
  const { contactId } = useParams({ from: "/contacts/$contactId" });
  const { view = "timeline" } = useSearch({ from: "/contacts/$contactId" });
  const navigate = useNavigate({ from: "/contacts/$contactId" });
  const id = Number(contactId);
  const contact = useContact(id);

  if (contact.isLoading) {
    return (
      <div className="mx-auto max-w-[1440px] space-y-3">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-8 w-2/3" />
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_320px]">
          <Skeleton className="h-80" />
          <Skeleton className="h-80" />
        </div>
      </div>
    );
  }
  if (contact.error || !contact.data) {
    const msg = contact.error instanceof ApiError ? contact.error.message : "Could not load this contact.";
    return <p className="text-sm text-[var(--color-destructive)]">{msg}</p>;
  }
  const c = contact.data;
  return (
    <div className="mx-auto max-w-[1440px] space-y-3">
      {c.requested_id && (
        <p className="rounded-md bg-[var(--color-accent)] px-3 py-2 text-sm text-[var(--color-accent-foreground)]">
          Contact #{c.requested_id} was merged into this record (#{c.id}).
        </p>
      )}
      <ContactHeader c={c} />
      <ActionBar c={c} />
      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-3">
          <PinnedNotes id={c.id} />
          <div className="grid gap-3 xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
            <ContactInfoCard c={c} />
            <DetailsGrid c={c} />
          </div>
          <CustomFieldsCard c={c} />
          <Tabs value={view} onValueChange={(v) => navigate({ search: { view: v === "timeline" ? undefined : (v as ContactView) }, replace: true })}>
            <TabsList>
              <TabsTrigger value="timeline">Timeline</TabsTrigger>
              <TabsTrigger value="billing">Billing</TabsTrigger>
              <TabsTrigger value="email">Email history</TabsTrigger>
              <TabsTrigger value="files">Files &amp; archive</TabsTrigger>
            </TabsList>
            <TabsContent value="timeline" className="pt-3">
              <Timeline id={c.id} />
            </TabsContent>
            <TabsContent value="billing" className="pt-3">
              <BillingView id={c.id} />
            </TabsContent>
            <TabsContent value="email" className="pt-3">
              <EmailHistory id={c.id} />
            </TabsContent>
            <TabsContent value="files" className="pt-3">
              <FilesView id={c.id} />
            </TabsContent>
          </Tabs>
        </div>
        <RightRail c={c} />
      </div>
    </div>
  );
}
